import { resolveEmbedIframeSrc, resolveEmbedPosterSrc } from './videoEmbed';

const MARKDOWN_IMAGE_RE = /!\[([^\]]*)\]\((https?:\/\/[^)\s]+)\)/gi;
const ANCHOR_HREF_RE = /<a\b[^>]*\bhref\s*=\s*["']([^"']+)["'][^>]*>[\s\S]*?<\/a>/gi;
const BARE_HTTP_URL_RE = /https?:\/\/[^\s<)"'\]]+\)?/gi;

function escapeAttr(value) {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function trimUrl(url) {
  return String(url || '').replace(/[)\]>.,;:!]+$/g, '');
}

function isInAttr(full, index) {
  const before = full.slice(0, index);
  if (before.lastIndexOf('<') > before.lastIndexOf('>')) return true;
  const tail = before.slice(Math.max(0, before.length - 12)).toLowerCase();
  return /url\s*\(\s*["']?$/i.test(tail);
}

function imgTag(src, alt = '') {
  return `<img src="${escapeAttr(src)}" alt="${escapeAttr(alt)}" />`;
}

function videoTag(iframeSrc, poster) {
  const preview = poster
    ? `<img class="lesson-desc-video-poster" alt="" src="${escapeAttr(poster)}" />`
    : '';
  return (
    `<div class="lesson-desc-video">` +
    `<button type="button" class="lesson-desc-video-play" data-lesson-embed="${escapeAttr(iframeSrc)}" aria-label="Play video">` +
    preview +
    `<span class="lesson-desc-video-play-icon" aria-hidden="true"></span>` +
    `</button></div>`
  );
}

export function openLessonDescriptionImageLightbox(img) {
  if (!img?.src || document.querySelector('.lesson-desc-image-lightbox')) return;
  const overlay = document.createElement('div');
  overlay.className = 'lesson-desc-image-lightbox';
  overlay.innerHTML = `<img src="${escapeAttr(img.src)}" alt="${escapeAttr(img.alt || '')}" />`;
  const close = () => {
    overlay.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = e => {
    if (e.key === 'Escape') close();
  };
  overlay.addEventListener('click', close);
  document.addEventListener('keydown', onKey);
  document.body.appendChild(overlay);
}

/** Direct image URLs — file extensions, CDNs without extensions, and Loom screenshots (/i/). */
export function isDirectImageUrl(urlString) {
  if (!urlString || typeof urlString !== 'string') return false;
  const raw = trimUrl(urlString.trim());
  try {
    const u = new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`);
    const path = u.pathname.toLowerCase();
    const host = u.hostname.toLowerCase();

    // Loom screenshot: loom.com/i/{id}
    if (host.includes('loom.com') && /\/i\/[a-zA-Z0-9_-]+/i.test(u.pathname)) return true;

    if (/\.(png|jpe?g|gif|webp|svg|avif|bmp)(\?|$)/i.test(path) || /\.(png|jpe?g|gif|webp|svg|avif|bmp)(\?|$)/i.test(raw)) {
      return true;
    }
    if (/(?:^|\.)(?:images\.unsplash\.com|plus\.unsplash\.com|i\.imgur\.com|images\.pexels\.com|cdn\.pixabay\.com|res\.cloudinary\.com)$/i.test(host)) {
      return true;
    }
    const format = (u.searchParams.get('fm') || u.searchParams.get('format') || '').toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif'].includes(format)) return true;
    return u.searchParams.get('auto') === 'format' && /unsplash\.com$/i.test(host);
  } catch {
    return /\.(png|jpe?g|gif|webp|svg|avif|bmp)(\?|$)/i.test(raw);
  }
}

function mediaHtmlForUrl(url, { alt = '', posters } = {}) {
  if (isDirectImageUrl(url)) return imgTag(trimUrl(url), alt || '');

  const embedSrc = resolveEmbedIframeSrc(url);
  if (embedSrc) {
    const poster = (posters && posters[embedSrc]) || resolveEmbedPosterSrc(embedSrc);
    return videoTag(embedSrc, poster);
  }

  return null;
}

function linkTag(url, text) {
  const href = trimUrl(url);
  const label = text && String(text).trim() ? String(text).trim() : href;
  return `<a href="${escapeAttr(href)}" rel="noopener noreferrer" target="_blank">${escapeAttr(label)}</a>`;
}

function isInsideAnchor(full, index) {
  const before = full.slice(0, index).toLowerCase();
  return before.lastIndexOf('<a') > before.lastIndexOf('</a>');
}

/** Turn markdown images / bare URLs into <a> links for the manage editor. */
export function linkifyLessonDescriptionHtml(raw) {
  if (raw == null) return '';
  let html = String(raw);
  if (!html.trim()) return '';

  html = html.replace(MARKDOWN_IMAGE_RE, (_, alt, url) => linkTag(url, alt));

  html = html.replace(BARE_HTTP_URL_RE, (matched, offset) => {
    if (isInAttr(html, offset) || isInsideAnchor(html, offset)) return matched;
    const url = matched.endsWith(')') ? matched.slice(0, -1) : matched;
    return linkTag(url);
  });

  return html;
}

/** Convert markdown images / Loom / video / image URLs into HTML (learner view only). */
export function enrichLessonDescriptionHtml(raw, posters) {
  if (raw == null) return '';
  let html = String(raw);
  if (!html.trim()) return '';

  html = html.replace(MARKDOWN_IMAGE_RE, (full, alt, url) => mediaHtmlForUrl(url, { alt, posters }) || full);

  html = html.replace(ANCHOR_HREF_RE, (full, href) => mediaHtmlForUrl(href, { posters }) || full);

  html = html.replace(BARE_HTTP_URL_RE, (matched, offset) => {
    if (isInAttr(html, offset)) return matched;
    const url = matched.endsWith(')') ? matched.slice(0, -1) : matched;
    return mediaHtmlForUrl(url, { posters }) || matched;
  });

  return html;
}
